import React, { useMemo, useState, useEffect } from 'react';
import useApi from '../api';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import toastConfig from '../config/Toast';
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from '@/lib/utils';
import { 
  UserPlus, 
  MoreHorizontal, 
  Mail, 
  Trash2, 
  Settings, 
  User, 
  Building2, 
  Bell, 
  Clock, 
  MessageSquare,
  UserCheck,
  Activity,
  HelpCircle,
  PhoneForwarded,
  TrendingUp,
  ClipboardCheck,
  FileText,
  GitBranch,
  Users,
  Globe,
  Save,
  CreditCard,
  Fingerprint,
  Thermometer,
  ShieldCheck,
  ExternalLink,
  Copy,
  Flame,
  Milestone
} from 'lucide-react';
import Upgrade from '../modals/Upgrade';
import InviteModal from '../modals/InviteModal';
import BillingTab from './BillingTab';
import LifecycleStages from './LifecycleStages';
import SessionModerationSettings from './SessionModerationSettings';
import { useUser } from './UserContext';
import AgentTabHeader from '../agents/tabs/AgentTabHeader';

type DayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

interface DayAvailability {
  enabled: boolean;
  start: string;
  end: string;
}

type WeeklyAvailability = Record<DayKey, DayAvailability>;

interface AvailabilitySettings {
  enabled?: boolean;
  timezone?: string;
  weekly?: Partial<Record<DayKey, Partial<DayAvailability>>>;
}

interface UserSettings {
  interaction_notification?: boolean;
  new_lead_notification?: boolean;
  visitor_identified_notification?: boolean;
  warm_visitor_notification?: boolean;
  session_active_notification?: boolean;
  new_question_notification?: boolean;
  escalation_notification?: boolean;
  buyer_intent_notification?: boolean;
  assessment_notification?: boolean;
  report_generated_notification?: boolean;
  track_classification_notification?: boolean;
  high_fit_account_notification?: boolean;
  session_flagged_notification?: boolean;
  availability?: AvailabilitySettings;
}

const DAY_KEYS: DayKey[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS: Record<DayKey, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};

function getSystemTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

const DEFAULT_TIMEZONE = getSystemTimeZone();

const DEFAULT_WEEKLY_AVAILABILITY: WeeklyAvailability = {
  mon: { enabled: true, start: '09:00', end: '17:00' },
  tue: { enabled: true, start: '09:00', end: '17:00' },
  wed: { enabled: true, start: '09:00', end: '17:00' },
  thu: { enabled: true, start: '09:00', end: '17:00' },
  fri: { enabled: true, start: '09:00', end: '17:00' },
  sat: { enabled: false, start: '09:00', end: '17:00' },
  sun: { enabled: false, start: '09:00', end: '17:00' },
};

function normalizeAvailability(raw: AvailabilitySettings | undefined): {
  enabled: boolean;
  timezone: string;
  weekly: WeeklyAvailability;
} {
  const enabled = Boolean(raw?.enabled);
  const timezone = typeof raw?.timezone === 'string' && raw.timezone ? raw.timezone : DEFAULT_TIMEZONE;

  const weeklyRaw = raw?.weekly || {};

  const weekly = DAY_KEYS.reduce((acc, day) => {
    const base = DEFAULT_WEEKLY_AVAILABILITY[day];
    const dayRaw = (weeklyRaw as any)?.[day] || {};
    acc[day] = {
      enabled: typeof dayRaw.enabled === 'boolean' ? dayRaw.enabled : base.enabled,
      start: typeof dayRaw.start === 'string' && dayRaw.start ? dayRaw.start : base.start,
      end: typeof dayRaw.end === 'string' && dayRaw.end ? dayRaw.end : base.end,
    };
    return acc;
  }, {} as WeeklyAvailability);

  return { enabled, timezone, weekly };
}

function getTimeZoneOptions(): string[] {
  const resolved = getSystemTimeZone();
  const anyIntl = Intl as any;

  if (typeof anyIntl?.supportedValuesOf === 'function') {
    try {
      const values = anyIntl.supportedValuesOf('timeZone') as string[];
      if (Array.isArray(values) && values.length > 0) {
        return values;
      }
    } catch {
      // Fall through to minimal list
    }
  }

  return Array.from(new Set([resolved, 'UTC'])).filter(Boolean);
}

interface UserProfileType {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  settings?: UserSettings;
  profile_photo_url?: string | null;
}

interface PendingInvite {
  id: string;
  email: string;
  status: string;
  created_at: string;
}

type TableRowType = UserProfileType | (PendingInvite & { type: 'invite' });
type EditingUser = Omit<UserProfileType, 'id'> & { id?: string };

// Notification configuration
const NOTIFICATIONS = [
  {
    key: 'interaction_notification',
    label: 'New Interaction',
    description: 'When a visitor interacts with the agent',
    icon: MessageSquare,
  },
  {
    key: 'new_lead_notification',
    label: 'New Contact',
    description: 'When a visitor submits their email',
    icon: UserCheck,
  },
  {
    key: 'visitor_identified_notification',
    label: 'Visitor Identified',
    description: 'When an anonymous visitor is identified by an ID provider',
    icon: Fingerprint,
  },
  {
    key: 'warm_visitor_notification',
    label: 'Warm Visitor',
    description: "When a visitor's browsing shows buying intent (pricing, demo or contact pages, product-page depth, return visits)",
    icon: Thermometer,
  },
  {
    key: 'session_active_notification',
    label: 'Session Active',
    description: 'When an existing session becomes active again',
    icon: Activity,
  },
  {
    key: 'new_question_notification',
    label: 'Unanswered Question',
    description: 'When the AI cannot answer a question',
    icon: HelpCircle,
  },
  {
    key: 'escalation_notification',
    label: 'Escalation',
    description: 'When a conversation is escalated',
    icon: PhoneForwarded,
  },
  {
    key: 'buyer_intent_notification',
    label: 'Buyer Intent',
    description: 'When a visitor expresses buyer intent',
    icon: TrendingUp,
  },
  {
    key: 'assessment_notification',
    label: 'Assessment Complete',
    description: 'When a visitor completes an assessment',
    icon: ClipboardCheck,
  },
  {
    key: 'report_generated_notification',
    label: 'Report Generated',
    description: 'When a report is generated for a visitor',
    icon: FileText,
  },
  {
    key: 'track_classification_notification',
    label: 'Visitor Classification',
    description: 'When a visitor is classified into a workflow track',
    icon: GitBranch,
  },
  {
    key: 'high_fit_account_notification',
    label: 'High-Fit Account',
    description: 'When account research scores an identified company above your fit threshold',
    icon: Flame,
  },
  {
    key: 'session_flagged_notification',
    label: 'Session Flagged',
    description: 'When session moderation finds a visitor acting in bad faith',
    icon: ShieldCheck,
  },
] as const;

const RB2B_TERMS_URL = 'https://aimdoc.ai/legal/visitor-identification-agreement';
const RB2B_COOKIE_DISCLOSURE = `When you visit or log in to our website, cookies and similar technologies may be used by our online data partners or vendors to associate these activities with other personal information they or others have about you, including by association with your email or online profiles. We (or service providers on our behalf) may then send communications and marketing to these emails or profiles. You may opt out of receiving this advertising by visiting https://app.retention.com/optout.`;

type IdentificationUsage = {
  count: number;
  limit: number;
  has_access: boolean;
  limit_exceeded: boolean;
  plan_limit: number;
  credit_budget: number | null;
  credits_per_resolution: number;
  credits_used: number;
  period_start: string;
  period_end: string;
};

type IdentificationSettings = {
  available: boolean;
  enabled: boolean;
  identify_all_visitors: boolean;
  identify_warm_visitors: boolean;
  customer_terms_accepted: boolean;
  domains: string[];
  resolution_limit: number | null;
  credit_budget: number | null;
  usage: IdentificationUsage;
};

const formatWholeNumber = (value: number) => Math.round(value).toLocaleString();

type IdentificationTiming = 'conversation' | 'warm' | 'all';

const IDENTIFICATION_TIMING_OPTIONS: Array<{
  value: IdentificationTiming;
  label: string;
  description: string;
  patch: { identify_all_visitors?: boolean; identify_warm_visitors?: boolean };
  toast: string;
}> = [
  {
    value: 'conversation',
    label: 'Visitors who start a conversation',
    description: 'Identify only when a visitor chats with your agent. Uses the fewest credits.',
    patch: { identify_all_visitors: false, identify_warm_visitors: false },
    toast: 'Identifying visitors who chat',
  },
  {
    value: 'warm',
    label: 'Warm visitors',
    description:
      'Identify once browsing shows intent: a pricing, demo or contact page, three or more product pages, a return visit, or a buying call to action. Blog readers and bounces stay anonymous.',
    patch: { identify_warm_visitors: true },
    toast: 'Identifying warm visitors',
  },
  {
    value: 'all',
    label: 'Every visitor',
    description: 'Identify as soon as anyone lands. Uses the most credits and can hit your limit early in the cycle.',
    patch: { identify_all_visitors: true },
    toast: 'Identifying every visitor',
  },
];

const identificationTimingFor = (settings: IdentificationSettings): IdentificationTiming => {
  if (settings.identify_all_visitors) return 'all';
  if (settings.identify_warm_visitors) return 'warm';
  return 'conversation';
};

// Notification toggle component
interface NotificationToggleProps {
  notificationKey: string;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  isEnabled: boolean;
  onChange: (key: string, value: boolean) => void;
}

function NotificationToggle({ notificationKey, label, description, icon: Icon, isEnabled, onChange }: NotificationToggleProps) {
  return (
    <div
      className="flex min-w-0 items-center justify-between gap-4 rounded-lg border bg-card p-4 transition-colors hover:border-foreground/15"
    >
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border bg-muted/30 text-muted-foreground">
          <Icon className="h-3.5 w-3.5" />
        </div>
        <div className="min-w-0">
          <span className="text-sm font-medium">{label}</span>
          <p className="mt-0.5 text-xs leading-5 text-muted-foreground">
            {description}
          </p>
        </div>
      </div>
      <Switch
        className="shrink-0"
        checked={isEnabled}
        onCheckedChange={(checked) => onChange(notificationKey, checked)}
      />
    </div>
  );
}

function UserProfile() {
  const { refreshOrganization, refreshUser } = useUser();
  const [organizationName, setOrganizationName] = useState('');
  const { get, put, post, remove } = useApi();
  const [currentUserSettings, setCurrentUserSettings] = useState<Record<string, any>>({});
  const [organizationSettings, setOrganizationSettings] = useState<Record<string, any>>({});
  const [rb2bTermsAccepted, setRb2bTermsAccepted] = useState<boolean>(false);
  const [isRb2bTermsSaving, setIsRb2bTermsSaving] = useState<boolean>(false);
  const [identification, setIdentification] = useState<IdentificationSettings | null>(null);
  const [identificationSaving, setIdentificationSaving] = useState<boolean>(false);
  const [identificationDomainInput, setIdentificationDomainInput] = useState('');
  const [identificationBudgetInput, setIdentificationBudgetInput] = useState('');
  
  // Notification states
  const [notifications, setNotifications] = useState<Record<string, boolean>>({
    interaction_notification: false,
    new_lead_notification: false,
    visitor_identified_notification: false,
    warm_visitor_notification: false,
    session_active_notification: false,
    new_question_notification: false,
    escalation_notification: false,
    buyer_intent_notification: false,
    assessment_notification: false,
    report_generated_notification: false,
    track_classification_notification: false,
    high_fit_account_notification: false,
    session_flagged_notification: false,
  });

  // Working hours / availability
  const [availabilityEnabled, setAvailabilityEnabled] = useState<boolean>(false);
  const [availabilityTimezone, setAvailabilityTimezone] = useState<string>(DEFAULT_TIMEZONE);
  const [availabilityWeekly, setAvailabilityWeekly] = useState<WeeklyAvailability>(
    () => normalizeAvailability(undefined).weekly
  );

  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | null>(null);
  const [isProfilePhotoSaving, setIsProfilePhotoSaving] = useState(false);
  const [userRole, setUserRole] = useState<string>('');
  const [organizationUsers, setOrganizationUsers] = useState<UserProfileType[]>([]);
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<EditingUser | null>(null);
  const [editedUserRole, setEditedUserRole] = useState<string>('');
  
  // Edited notification states
  const [editedNotifications, setEditedNotifications] = useState<Record<string, boolean>>({});

  // Working hours for edit dialog
  const [editedAvailabilityEnabled, setEditedAvailabilityEnabled] = useState<boolean>(false);
  const [editedAvailabilityTimezone, setEditedAvailabilityTimezone] = useState<string>(DEFAULT_TIMEZONE);
  const [editedAvailabilityWeekly, setEditedAvailabilityWeekly] = useState<WeeklyAvailability>(
    () => normalizeAvailability(undefined).weekly
  );

  const [upgradeModalVisible, setUpgradeModalVisible] = useState(false);
  
  // Tab navigation state
  const [selectedTab, setSelectedTab] = useState<string>('user:profile');

  const timeZones = useMemo(() => getTimeZoneOptions(), []);
  const identificationAvailable = Boolean(identification?.available);
  const hasRb2bComplianceAccess = identificationAvailable;

  const handleTabChange = (value: string) => {
    setSelectedTab(value);
  };

  const updateNotification = (key: string, value: boolean) => {
    setNotifications(prev => ({ ...prev, [key]: value }));
  };

  const updateEditedNotification = (key: string, value: boolean) => {
    setEditedNotifications(prev => ({ ...prev, [key]: value }));
  };

  const updateAvailabilityDay = (day: DayKey, patch: Partial<DayAvailability>) => {
    setAvailabilityWeekly(prev => ({
      ...prev,
      [day]: { ...prev[day], ...patch },
    }));
  };

  const updateEditedAvailabilityDay = (day: DayKey, patch: Partial<DayAvailability>) => {
    setEditedAvailabilityWeekly(prev => ({
      ...prev,
      [day]: { ...prev[day], ...patch },
    }));
  };

  const handleRb2bTermsToggle = async (nextAccepted: boolean) => {
    const previousAccepted = rb2bTermsAccepted;
    setRb2bTermsAccepted(nextAccepted);
    setIsRb2bTermsSaving(true);

    const existingRb2bConfig =
      organizationSettings.rb2b_oem && typeof organizationSettings.rb2b_oem === 'object'
        ? organizationSettings.rb2b_oem
        : {};
    const nextOrganizationSettings = {
      ...organizationSettings,
      rb2b_oem: {
        ...existingRb2bConfig,
        customer_terms_accepted: nextAccepted,
      },
    };

    try {
      await put(`/users/me/organization`, {
        name: organizationName,
        settings: nextOrganizationSettings,
      });
      setOrganizationSettings(nextOrganizationSettings);
      await refreshOrganization();
      await loadIdentification();
      toast.success(
        nextAccepted
          ? 'Visitor identification terms accepted'
          : 'Visitor identification terms acceptance removed',
        toastConfig,
      );
    } catch (error: any) {
      console.error('Error updating visitor identification terms acceptance:', error);
      setRb2bTermsAccepted(previousAccepted);
      toast.error('Failed to update terms acceptance', toastConfig);
    } finally {
      setIsRb2bTermsSaving(false);
    }
  };

  const handleSave = async (e?: any) => {
    e?.preventDefault();
    if (!organizationName) {
      toast.error('Organization name cannot be empty', toastConfig);
      return;
    }

    try {
      // The identification block has its own endpoints; the generic save only
      // records the terms acknowledgement (the server ignores anything else).
      const { rb2b_oem: _ignoredRb2bConfig, ...otherOrganizationSettings } = organizationSettings;
      const nextOrganizationSettings = hasRb2bComplianceAccess || rb2bTermsAccepted
        ? {
            ...otherOrganizationSettings,
            rb2b_oem: { customer_terms_accepted: rb2bTermsAccepted },
          }
        : otherOrganizationSettings;

      await put(`/users/me/organization`, {
        name: organizationName,
        settings: nextOrganizationSettings,
      });

      await put(`/users/me`, {
        first_name: firstName,
        last_name: lastName,
        email: email,
        settings: {
          ...(currentUserSettings || {}),
          ...notifications,
          availability: {
            enabled: availabilityEnabled,
            timezone: availabilityTimezone,
            weekly: availabilityWeekly,
          },
        }
      });

      setCurrentUserSettings(prev => ({
        ...(prev || {}),
        ...notifications,
        availability: {
          enabled: availabilityEnabled,
          timezone: availabilityTimezone,
          weekly: availabilityWeekly,
        },
      }));
      setOrganizationSettings(nextOrganizationSettings);
      await refreshOrganization();

      toast.success('Settings updated successfully', toastConfig);
    } catch (error: any) {
      console.error('Error updating settings:', error);
      toast.error('Failed to update settings', toastConfig);
    }
  };

  const profileInitials = useMemo(() => {
    const firstInitial = firstName?.trim()?.[0] || '';
    const lastInitial = lastName?.trim()?.[0] || '';
    const emailInitial = email?.trim()?.[0] || 'A';
    return `${firstInitial}${lastInitial}`.trim().toUpperCase() || emailInitial.toUpperCase();
  }, [email, firstName, lastName]);

  const handleProfilePhotoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    setIsProfilePhotoSaving(true);

    try {
      const response = await post('/users/me/profile-photo', formData);
      setProfilePhotoUrl(response.data.profile_photo_url || null);
      await refreshUser();
      toast.success('Profile photo updated', toastConfig);
    } catch (error: any) {
      console.error('Error uploading profile photo:', error);
      toast.error(error?.detail || 'Failed to upload profile photo', toastConfig);
    } finally {
      setIsProfilePhotoSaving(false);
    }
  };

  const handleProfilePhotoRemove = async () => {
    setIsProfilePhotoSaving(true);

    try {
      const response = await remove('/users/me/profile-photo');
      setProfilePhotoUrl(response.data.profile_photo_url || null);
      await refreshUser();
      toast.success('Profile photo removed', toastConfig);
    } catch (error: any) {
      console.error('Error removing profile photo:', error);
      toast.error(error?.detail || 'Failed to remove profile photo', toastConfig);
    } finally {
      setIsProfilePhotoSaving(false);
    }
  };

  useEffect(() => {
    const getUser = async () => {
      try {
        const response = await get(`/users/me`);
        setFirstName(response.data.first_name);
        setLastName(response.data.last_name);
        setEmail(response.data.email);
        setProfilePhotoUrl(response.data.profile_photo_url || null);
        const settings = response.data.settings || {};
        setCurrentUserSettings(settings);
        
        setNotifications({
          interaction_notification: settings.interaction_notification || false,
          new_lead_notification: settings.new_lead_notification || false,
          visitor_identified_notification: settings.visitor_identified_notification || false,
          warm_visitor_notification: settings.warm_visitor_notification || false,
          session_active_notification: settings.session_active_notification || false,
          new_question_notification: settings.new_question_notification || false,
          escalation_notification: settings.escalation_notification || false,
          buyer_intent_notification: settings.buyer_intent_notification || false,
          assessment_notification: settings.assessment_notification || false,
          report_generated_notification: settings.report_generated_notification || false,
          track_classification_notification: settings.track_classification_notification || false,
          high_fit_account_notification: settings.high_fit_account_notification || false,
          session_flagged_notification: settings.session_flagged_notification || false,
        });
        
        const normalizedAvailability = normalizeAvailability(settings.availability);
        setAvailabilityEnabled(normalizedAvailability.enabled);
        setAvailabilityTimezone(normalizedAvailability.timezone);
        setAvailabilityWeekly(normalizedAvailability.weekly);
        setUserRole(response.data.role);

        if (response.data.role === 'admin') {
          getOrganizationUsers();
          getPendingInvites();
        }
      } catch (error: any) {
        console.error('Error fetching user:', error);
      }
    };

    const getOrganization = async () => {
      try {
        const response = await get(`/users/me/organization`);
        setOrganizationName(response.data.name);
        const nextOrganizationSettings =
          response.data.settings && typeof response.data.settings === 'object'
            ? response.data.settings
            : {};
        const rb2bConfig =
          nextOrganizationSettings.rb2b_oem && typeof nextOrganizationSettings.rb2b_oem === 'object'
            ? nextOrganizationSettings.rb2b_oem
            : {};
        setOrganizationSettings(nextOrganizationSettings);
        setRb2bTermsAccepted(Boolean(rb2bConfig.customer_terms_accepted));
      } catch (error: any) {
        console.error('Error fetching organization:', error);
      }
    };

    const getOrganizationUsers = async () => {
      try {
        const response = await get(`/users/`);
        setOrganizationUsers(response.data);
      } catch (error: any) {
        console.error('Error fetching organization users:', error);
      }
    };

    const getPendingInvites = async () => {
      try {
        const response = await get(`/invites/pending`);
        setPendingInvites(response.data);
      } catch (error: any) {
        console.error('Error fetching pending invites:', error);
      }
    };

    getUser();
    getOrganization();
    loadIdentification();
  }, []);

  const handleInvitesSent = async () => {
    try {
      const response = await get(`/invites/pending`);
      setPendingInvites(response.data);
    } catch (error: any) {
      console.error('Error fetching pending invites:', error);
    }
  };

  const isUser = (row: TableRowType): row is UserProfileType => {
    return !('type' in row);
  };

  const loadIdentification = async () => {
    try {
      const response = await get(`/users/me/organization/identification`);
      const next: IdentificationSettings = response.data;
      setIdentification(next);
      setRb2bTermsAccepted(Boolean(next.customer_terms_accepted));
      setIdentificationBudgetInput(
        typeof next.credit_budget === 'number' ? String(next.credit_budget) : ''
      );
    } catch (error: any) {
      console.error('Error fetching identification settings:', error);
    }
  };

  const updateIdentification = async (
    patch: {
      enabled?: boolean;
      identify_all_visitors?: boolean;
      identify_warm_visitors?: boolean;
      credit_budget?: number | null;
    },
    successMessage: string,
  ) => {
    setIdentificationSaving(true);
    try {
      const response = await put(`/users/me/organization/identification`, patch);
      const next: IdentificationSettings = response.data;
      setIdentification(next);
      setIdentificationBudgetInput(
        typeof next.credit_budget === 'number' ? String(next.credit_budget) : ''
      );
      toast.success(successMessage, toastConfig);
    } catch (error: any) {
      console.error('Error updating identification settings:', error);
      toast.error(
        typeof error?.detail === 'string' ? error.detail : 'Failed to update visitor identification',
        toastConfig,
      );
    } finally {
      setIdentificationSaving(false);
    }
  };

  const handleSaveIdentificationBudget = async () => {
    const trimmed = identificationBudgetInput.trim();
    let budget: number | null = null;
    if (trimmed !== '') {
      const parsed = Number.parseInt(trimmed, 10);
      if (!Number.isFinite(parsed) || parsed < 0) {
        toast.error('Monthly budget must be a whole number of credits', toastConfig);
        return;
      }
      budget = parsed;
    }
    await updateIdentification(
      { credit_budget: budget },
      budget === null ? 'Identification budget removed' : 'Identification budget saved',
    );
  };

  const handleAddIdentificationDomain = async () => {
    const domain = identificationDomainInput.trim();
    if (!domain) return;
    setIdentificationSaving(true);
    try {
      const response = await post(`/users/me/organization/identification/domains`, { domain });
      setIdentification(response.data);
      setIdentificationDomainInput('');
      toast.success('Website added', toastConfig);
    } catch (error: any) {
      console.error('Error adding identification domain:', error);
      toast.error(typeof error?.detail === 'string' ? error.detail : 'Failed to add website', toastConfig);
    } finally {
      setIdentificationSaving(false);
    }
  };

  const handleRemoveIdentificationDomain = async (domain: string) => {
    setIdentificationSaving(true);
    try {
      const response = await remove(
        `/users/me/organization/identification/domains/${encodeURIComponent(domain)}`
      );
      setIdentification(response.data);
      toast.success('Website removed', toastConfig);
    } catch (error: any) {
      console.error('Error removing identification domain:', error);
      toast.error(typeof error?.detail === 'string' ? error.detail : 'Failed to remove website', toastConfig);
    } finally {
      setIdentificationSaving(false);
    }
  };

  const handleCopyRb2bCookieLanguage = async () => {
    try {
      await navigator.clipboard.writeText(RB2B_COOKIE_DISCLOSURE);
      toast.success('Cookie banner language copied', toastConfig);
    } catch (error) {
      console.error('Error copying RB2B cookie banner language:', error);
      toast.error('Failed to copy cookie banner language', toastConfig);
    }
  };

  const tableData: TableRowType[] = [
    ...organizationUsers,
    ...pendingInvites.map(invite => ({ ...invite, type: 'invite' as const }))
  ];

  const handleOpenEditDialog = (user: UserProfileType) => {
    setSelectedUser(user);
    setEditedUserRole(user.role);
    const settings = user.settings || {};
    
    setEditedNotifications({
      interaction_notification: settings.interaction_notification || false,
      new_lead_notification: settings.new_lead_notification || false,
      visitor_identified_notification: settings.visitor_identified_notification || false,
      warm_visitor_notification: settings.warm_visitor_notification || false,
      session_active_notification: settings.session_active_notification || false,
      new_question_notification: settings.new_question_notification || false,
      escalation_notification: settings.escalation_notification || false,
      buyer_intent_notification: settings.buyer_intent_notification || false,
      assessment_notification: settings.assessment_notification || false,
      report_generated_notification: settings.report_generated_notification || false,
      track_classification_notification: settings.track_classification_notification || false,
      high_fit_account_notification: settings.high_fit_account_notification || false,
      session_flagged_notification: settings.session_flagged_notification || false,
    });
    
    const normalizedAvailability = normalizeAvailability(settings.availability);
    setEditedAvailabilityEnabled(normalizedAvailability.enabled);
    setEditedAvailabilityTimezone(normalizedAvailability.timezone);
    setEditedAvailabilityWeekly(normalizedAvailability.weekly);
    setIsEditDialogOpen(true);
  };

  const handleUserUpdate = async () => {
    if (!selectedUser || !selectedUser.id) return;

    const baseSettings = (selectedUser.settings || {}) as Record<string, any>;
    const updatedSettings = {
      ...baseSettings,
      ...editedNotifications,
      availability: {
        enabled: editedAvailabilityEnabled,
        timezone: editedAvailabilityTimezone,
        weekly: editedAvailabilityWeekly,
      },
    };

    const payload = {
      role: editedUserRole,
      settings: updatedSettings,
    };

    try {
      const response = await put(`/users/${selectedUser.id}`, payload);
      setOrganizationUsers(prevUsers =>
        prevUsers.map(u => (u.id === selectedUser.id ? { ...u, ...response.data } : u))
      );
      toast.success('User updated successfully', toastConfig);
      setIsEditDialogOpen(false);
      setSelectedUser(null);
    } catch (error: any) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user', toastConfig);
    }
  };

  const handleResendInvite = async (invite: PendingInvite & { type: 'invite' }) => {
    try {
      await post('/invite/', { emails: [invite.email] });
      toast.success('Invite resent successfully', toastConfig);
    } catch (error) {
      console.error('Error resending invite:', error);
      toast.error('Failed to resend invite', toastConfig);
    }
  };

  const handleDeleteInvite = async (invite: PendingInvite & { type: 'invite' }) => {
    try {
      await remove(`/invites/${invite.id}`);
      setPendingInvites(pendingInvites.filter(i => i.id !== invite.id));
      toast.success('Invite deleted successfully', toastConfig);
    } catch (error) {
      console.error('Error deleting invite:', error);
      toast.error('Failed to delete invite', toastConfig);
    }
  };

  const handleApproveAccessRequest = async (invite: PendingInvite & { type: 'invite' }) => {
    try {
      await post(`/invites/${invite.id}/approve-access-request`, {});
      setPendingInvites(prev => prev.filter(i => i.id !== invite.id));
      const usersResponse = await get(`/users/`);
      setOrganizationUsers(usersResponse.data);
      toast.success('Access request approved', toastConfig);
    } catch (error) {
      console.error('Error approving access request:', error);
      toast.error('Failed to approve access request', toastConfig);
    }
  };

  const handleRemoveUserAccess = async (user: UserProfileType) => {
    const userLabel = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email;
    const confirmed = window.confirm(
      `Remove ${userLabel}'s access to this organization? They will need to request access again to rejoin.`
    );
    if (!confirmed) {
      return;
    }

    try {
      await remove(`/users/${user.id}`);
      setOrganizationUsers(organizationUsers.filter(u => u.id !== user.id));
      toast.success('User access removed successfully', toastConfig);
    } catch (error: any) {
      console.error('Error removing user access:', error);
      toast.error(error.detail || 'Failed to remove user access', toastConfig);
    }
  };

  const enabledNotificationsCount = Object.values(notifications).filter(Boolean).length;

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      <form onSubmit={handleSave} className="flex h-full w-full flex-col overflow-hidden">
        <div className="flex shrink-0 items-center px-4 pb-3 pt-4">
          <div className="min-w-0">
            <h1 className="text-sm font-bold tracking-tight">Account Settings</h1>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Manage your personal and organization preferences
            </p>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 gap-4 px-4 pb-4">
          <Tabs 
            value={selectedTab} 
            onValueChange={handleTabChange} 
            orientation="vertical"
            className="flex min-h-0 w-full gap-4"
          >
            <div className="w-14 shrink-0 overflow-hidden rounded-lg border bg-card md:w-56">
              <TabsList className="flex h-full w-full flex-col items-stretch justify-start gap-1 overflow-y-auto bg-transparent p-2 md:p-3">
                <p className="hidden px-2 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground md:block">
                  Personal
                </p>
                <TabsTrigger
                  value="user:profile"
                  title="Profile"
                  className="relative flex w-full justify-center gap-2 px-2 md:justify-start md:px-3"
                >
                  {selectedTab === 'user:profile' && <span className="absolute inset-y-2 left-0 w-0.5 rounded-full bg-primary" />}
                  <User className="h-4 w-4 shrink-0" />
                  <span className="hidden md:inline">Profile</span>
                </TabsTrigger>
                <TabsTrigger
                  value="user:notifications"
                  title="Notifications"
                  className="relative flex w-full justify-center gap-2 px-2 md:justify-start md:px-3"
                >
                  {selectedTab === 'user:notifications' && <span className="absolute inset-y-2 left-0 w-0.5 rounded-full bg-primary" />}
                  <Bell className="h-4 w-4 shrink-0" />
                  <span className="hidden md:inline">Notifications</span>
                  {enabledNotificationsCount > 0 && (
                    <Badge variant="secondary" className="ml-auto hidden h-4 px-1.5 text-[10px] md:inline-flex">
                      {enabledNotificationsCount}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger
                  value="user:hours"
                  title="Working Hours"
                  className="relative flex w-full justify-center gap-2 px-2 md:justify-start md:px-3"
                >
                  {selectedTab === 'user:hours' && <span className="absolute inset-y-2 left-0 w-0.5 rounded-full bg-primary" />}
                  <Clock className="h-4 w-4 shrink-0" />
                  <span className="hidden md:inline">Working Hours</span>
                </TabsTrigger>

                {userRole === 'admin' && (
                  <>
                    <div className="my-1 border-t border-border/60" />
                    <p className="hidden px-2 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground md:block">
                      Organization
                    </p>
                    <TabsTrigger
                      value="org:details"
                      title="Organization Details"
                      className="relative flex w-full justify-center gap-2 px-2 md:justify-start md:px-3"
                    >
                      {selectedTab === 'org:details' && <span className="absolute inset-y-2 left-0 w-0.5 rounded-full bg-primary" />}
                      <Building2 className="h-4 w-4 shrink-0" />
                      <span className="hidden md:inline">Details</span>
                    </TabsTrigger>
                    <TabsTrigger
                      value="org:team"
                      title="Team"
                      className="relative flex w-full justify-center gap-2 px-2 md:justify-start md:px-3"
                    >
                      {selectedTab === 'org:team' && <span className="absolute inset-y-2 left-0 w-0.5 rounded-full bg-primary" />}
                      <Users className="h-4 w-4 shrink-0" />
                      <span className="hidden md:inline">Team</span>
                      <Badge variant="secondary" className="ml-auto hidden h-4 px-1.5 text-[10px] md:inline-flex">
                        {organizationUsers.length}
                      </Badge>
                    </TabsTrigger>
                    <TabsTrigger
                      value="org:stages"
                      title="Lifecycle Stages"
                      className="relative flex w-full justify-center gap-2 px-2 md:justify-start md:px-3"
                    >
                      {selectedTab === 'org:stages' && <span className="absolute inset-y-2 left-0 w-0.5 rounded-full bg-primary" />}
                      <Milestone className="h-4 w-4 shrink-0" />
                      <span className="hidden md:inline">Lifecycle Stages</span>
                    </TabsTrigger>
                    <TabsTrigger
                      value="org:billing"
                      title="Billing"
                      className="relative flex w-full justify-center gap-2 px-2 md:justify-start md:px-3"
                    >
                      {selectedTab === 'org:billing' && <span className="absolute inset-y-2 left-0 w-0.5 rounded-full bg-primary" />}
                      <CreditCard className="h-4 w-4 shrink-0" />
                      <span className="hidden md:inline">Billing</span>
                    </TabsTrigger>
                  </>
                )}
              </TabsList>
            </div>

            <div className="min-w-0 flex-1 overflow-hidden rounded-lg border bg-background">
              <ScrollArea className="h-full w-full">
                <div className="p-4">
                  {/* Profile Tab */}
                  <TabsContent value="user:profile" className="mt-0">
                    <AgentTabHeader
                      icon={User}
                      title="Profile"
                      description="Manage your personal information and chat identity."
                      actions={(
                        <Button type="submit" size="sm" className="gap-2">
                          <Save className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">Save changes</span>
                          <span className="sm:hidden">Save</span>
                        </Button>
                      )}
                    />
                    <div className="mx-auto w-full max-w-4xl space-y-8 py-6">
                      <section className="space-y-3">
                        <div>
                          <h3 className="text-sm font-semibold">Profile photo</h3>
                          <p className="mt-1 text-xs leading-5 text-muted-foreground">
                            Shown to visitors when you take over a live conversation.
                          </p>
                        </div>
                        <div className="flex flex-col gap-4 rounded-lg border bg-card p-4 sm:flex-row sm:items-center sm:justify-between">
                          <div className="flex min-w-0 items-center gap-4">
                            <Avatar className="h-14 w-14 shrink-0 border">
                              <AvatarImage src={profilePhotoUrl || undefined} alt="Profile photo" />
                              <AvatarFallback className="bg-muted text-base font-semibold text-muted-foreground">
                                {profileInitials}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0">
                              <p className="text-sm font-medium">
                                {profilePhotoUrl ? 'Your current photo' : 'Add a profile photo'}
                              </p>
                              <p className="mt-0.5 text-xs leading-5 text-muted-foreground">
                                PNG, JPG, GIF, or WEBP up to 5 MB.
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <Button type="button" variant="outline" size="sm" disabled={isProfilePhotoSaving} asChild>
                              <Label htmlFor="profilePhotoUpload" className="cursor-pointer">
                                {profilePhotoUrl ? 'Replace Photo' : 'Upload Photo'}
                              </Label>
                            </Button>
                            <Input
                              id="profilePhotoUpload"
                              type="file"
                              accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                              className="hidden"
                              onChange={handleProfilePhotoChange}
                              disabled={isProfilePhotoSaving}
                            />
                            {profilePhotoUrl && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                disabled={isProfilePhotoSaving}
                                onClick={handleProfilePhotoRemove}
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                                Remove
                              </Button>
                            )}
                          </div>
                        </div>
                      </section>

                      <section className="space-y-3">
                        <div>
                          <h3 className="text-sm font-semibold">Personal details</h3>
                          <p className="mt-1 text-xs leading-5 text-muted-foreground">
                            Your name and account access information.
                          </p>
                        </div>
                        <div className="divide-y overflow-hidden rounded-lg border bg-card">
                          <div className="grid gap-4 p-4 md:grid-cols-[190px_minmax(0,1fr)] md:gap-8">
                            <div>
                              <Label htmlFor="firstName" className="text-sm font-semibold">Name</Label>
                              <p className="mt-1 text-xs leading-5 text-muted-foreground">Used across your workspace.</p>
                            </div>
                            <div className="grid gap-4 sm:grid-cols-2">
                              <div className="space-y-2">
                                <Label htmlFor="firstName" className="text-xs font-medium">First name</Label>
                                <Input
                                  id="firstName"
                                  value={firstName}
                                  onChange={(e) => setFirstName(e.target.value)}
                                  placeholder="First name"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="lastName" className="text-xs font-medium">Last name</Label>
                                <Input
                                  id="lastName"
                                  value={lastName}
                                  onChange={(e) => setLastName(e.target.value)}
                                  placeholder="Last name"
                                />
                              </div>
                            </div>
                          </div>
                          <div className="grid gap-4 p-4 md:grid-cols-[190px_minmax(0,1fr)] md:gap-8">
                            <div>
                              <Label htmlFor="email" className="text-sm font-semibold">Email address</Label>
                              <p className="mt-1 text-xs leading-5 text-muted-foreground">Contact support to change it.</p>
                            </div>
                            <Input
                              id="email"
                              type="email"
                              value={email}
                              disabled
                              className="bg-muted/30"
                            />
                          </div>
                          <div className="grid gap-4 p-4 md:grid-cols-[190px_minmax(0,1fr)] md:gap-8">
                            <div>
                              <Label className="text-sm font-semibold">Workspace role</Label>
                              <p className="mt-1 text-xs leading-5 text-muted-foreground">Controls organization permissions.</p>
                            </div>
                            <div className="flex items-center">
                              <Badge variant="outline" className="capitalize">
                                {userRole}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </section>
                    </div>
                  </TabsContent>

                  {/* Notifications Tab */}
                  <TabsContent value="user:notifications" className="mt-0">
                    <AgentTabHeader
                      icon={Bell}
                      title="Notifications"
                      description="Choose which activity should trigger an email notification."
                      actions={(
                        <Button type="submit" size="sm" className="gap-2">
                          <Save className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">Save changes</span>
                          <span className="sm:hidden">Save</span>
                        </Button>
                      )}
                    />
                    <div className="mx-auto w-full max-w-4xl py-6">
                      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                        {NOTIFICATIONS.map((notification) => (
                          <NotificationToggle
                            key={notification.key}
                            notificationKey={notification.key}
                            label={notification.label}
                            description={notification.description}
                            icon={notification.icon}
                            isEnabled={notifications[notification.key]}
                            onChange={updateNotification}
                          />
                        ))}
                      </div>
                    </div>
                  </TabsContent>

                  {/* Working Hours Tab */}
                  <TabsContent value="user:hours" className="mt-0">
                    <AgentTabHeader
                      icon={Clock}
                      title="Working Hours"
                      description="Set when you are available for escalations and live chat."
                      actions={(
                        <Button type="submit" size="sm" className="gap-2">
                          <Save className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">Save changes</span>
                          <span className="sm:hidden">Save</span>
                        </Button>
                      )}
                    />
                    <div className="mx-auto w-full max-w-4xl space-y-8 py-6">
                      <section className="space-y-3">
                        <div>
                          <h3 className="text-sm font-semibold">Availability</h3>
                          <p className="mt-1 text-xs leading-5 text-muted-foreground">
                            When disabled, you are treated as available around the clock.
                          </p>
                        </div>
                        <div className="flex items-center justify-between gap-4 rounded-lg border bg-card p-4">
                          <div className="flex min-w-0 items-center gap-3">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border bg-muted/30 text-muted-foreground">
                              <Clock className="h-3.5 w-3.5" />
                            </div>
                            <div className="min-w-0">
                              <span className="text-sm font-medium">Use working hours</span>
                              <p className="mt-0.5 text-xs leading-5 text-muted-foreground">
                                Limit escalation availability to the schedule below.
                              </p>
                            </div>
                          </div>
                          <Switch
                            className="shrink-0"
                            checked={availabilityEnabled}
                            onCheckedChange={setAvailabilityEnabled}
                          />
                        </div>
                      </section>

                      {availabilityEnabled && (
                        <section className="space-y-3">
                          <div>
                            <h3 className="text-sm font-semibold">Weekly schedule</h3>
                            <p className="mt-1 text-xs leading-5 text-muted-foreground">
                              Configure the time zone and active hours for each day.
                            </p>
                          </div>
                          <div className="divide-y overflow-hidden rounded-lg border bg-card">
                            <div className="grid gap-4 p-4 md:grid-cols-[190px_minmax(0,1fr)] md:gap-8">
                              <div>
                                <Label className="text-sm font-semibold">Time zone</Label>
                                <p className="mt-1 text-xs leading-5 text-muted-foreground">Applies to every day below.</p>
                              </div>
                            <Select value={availabilityTimezone} onValueChange={setAvailabilityTimezone}>
                              <SelectTrigger className="w-full max-w-md">
                                <Globe className="h-4 w-4 mr-2 text-muted-foreground" />
                                <SelectValue placeholder="Select a time zone" />
                              </SelectTrigger>
                              <SelectContent className="max-h-[320px]">
                                {timeZones.map((tz) => (
                                  <SelectItem key={tz} value={tz}>
                                    {tz}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            </div>
                            <div>
                              {DAY_KEYS.map((day, index) => (
                                <div 
                                  key={day} 
                                  className={cn(
                                    "grid gap-3 p-4 transition-colors sm:grid-cols-[120px_minmax(0,1fr)] sm:items-center",
                                    index !== DAY_KEYS.length - 1 && "border-b",
                                    availabilityWeekly[day].enabled 
                                      ? "bg-background" 
                                      : "bg-muted/20"
                                  )}
                                >
                                  <div className="flex items-center gap-3">
                                    <Switch
                                      checked={availabilityWeekly[day].enabled}
                                      onCheckedChange={(checked) => updateAvailabilityDay(day, { enabled: checked })}
                                    />
                                    <span className={cn(
                                      "text-sm font-medium",
                                      !availabilityWeekly[day].enabled && "text-muted-foreground"
                                    )}>
                                      {DAY_LABELS[day]}
                                    </span>
                                  </div>
                                  <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2">
                                    <Input
                                      type="time"
                                      value={availabilityWeekly[day].start}
                                      disabled={!availabilityWeekly[day].enabled}
                                      onChange={(e) => updateAvailabilityDay(day, { start: e.target.value })}
                                      className="min-w-0"
                                    />
                                    <span className="text-sm text-muted-foreground">to</span>
                                    <Input
                                      type="time"
                                      value={availabilityWeekly[day].end}
                                      disabled={!availabilityWeekly[day].enabled}
                                      onChange={(e) => updateAvailabilityDay(day, { end: e.target.value })}
                                      className="min-w-0"
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </section>
                      )}
                    </div>
                  </TabsContent>

                  {/* Organization Details Tab */}
                  {userRole === 'admin' && (
                    <TabsContent value="org:details" className="mt-0">
                      <AgentTabHeader
                        icon={Building2}
                        title="Organization"
                        description="Manage your workspace identity and compliance settings."
                        actions={(
                          <Button type="submit" size="sm" className="gap-2">
                            <Save className="h-3.5 w-3.5" />
                            <span className="hidden sm:inline">Save changes</span>
                            <span className="sm:hidden">Save</span>
                          </Button>
                        )}
                      />
                      <div className="mx-auto w-full max-w-4xl space-y-8 py-6">
                        <section className="space-y-3">
                          <div>
                            <h3 className="text-sm font-semibold">Organization details</h3>
                            <p className="mt-1 text-xs leading-5 text-muted-foreground">
                              The name shown throughout your Aimdoc workspace.
                            </p>
                          </div>
                          <div className="grid gap-4 rounded-lg border bg-card p-4 md:grid-cols-[190px_minmax(0,1fr)] md:gap-8">
                            <div>
                              <Label htmlFor="orgName" className="text-sm font-semibold">Organization name</Label>
                              <p className="mt-1 text-xs leading-5 text-muted-foreground">Visible to every team member.</p>
                            </div>
                            <Input
                              id="orgName"
                              value={organizationName}
                              onChange={(e) => setOrganizationName(e.target.value)}
                              placeholder="Enter organization name"
                              className="max-w-md"
                            />
                          </div>
                        </section>

                        {hasRb2bComplianceAccess && (
                          <section className="space-y-3">
                            <div>
                              <h3 className="text-sm font-semibold">Visitor identification compliance</h3>
                              <p className="mt-1 text-xs leading-5 text-muted-foreground">
                                Review the required terms and cookie disclosure before enabling visitor identification.
                              </p>
                            </div>
                            <div className="divide-y overflow-hidden rounded-lg border bg-card">
                              <div className="flex items-start gap-3 p-4">
                                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border bg-muted/30 text-muted-foreground">
                                  <ShieldCheck className="h-3.5 w-3.5" />
                                </div>
                                <Checkbox
                                  id="rb2bTermsAccepted"
                                  className="mt-1"
                                  checked={rb2bTermsAccepted}
                                  disabled={isRb2bTermsSaving}
                                  onCheckedChange={(checked) => handleRb2bTermsToggle(checked === true)}
                                />
                                <div className="min-w-0 flex-1">
                                  <Label htmlFor="rb2bTermsAccepted" className="text-sm font-medium leading-5">
                                    I confirm that my organization agrees to the website visitor identification terms.
                                  </Label>
                                  <p className="mt-1 text-xs leading-5 text-muted-foreground">
                                    This acknowledgement is stored as an organization compliance setting.
                                  </p>
                                  <div className="mt-3 flex flex-wrap items-center gap-3">
                                    <a
                                      href={RB2B_TERMS_URL}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                                    >
                                      Review terms
                                      <ExternalLink className="h-3 w-3" />
                                    </a>
                                    <Badge variant="outline" className="text-[10px]">
                                      {rb2bTermsAccepted ? 'Accepted' : 'Pending acceptance'}
                                    </Badge>
                                  </div>
                                </div>
                              </div>
                              <div className="p-4">
                                <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                                  <div>
                                    <Label htmlFor="rb2bCookieDisclosure" className="text-sm font-semibold">Cookie banner language</Label>
                                    <p className="mt-1 text-xs leading-5 text-muted-foreground">
                                      Suggested copy to review with your legal team before publishing.
                                    </p>
                                  </div>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={handleCopyRb2bCookieLanguage}
                                    className="gap-1.5"
                                  >
                                    <Copy className="h-3.5 w-3.5" />
                                    Copy language
                                  </Button>
                                </div>
                                <Textarea
                                  id="rb2bCookieDisclosure"
                                  value={RB2B_COOKIE_DISCLOSURE}
                                  readOnly
                                  className="min-h-[160px] bg-muted/20 text-xs leading-5"
                                />
                              </div>
                            </div>
                          </section>
                        )}

                        {hasRb2bComplianceAccess && identification && (() => {
                          const usage = identification.usage;
                          const budgetCeiling =
                            typeof identification.credit_budget === 'number' && usage.credits_per_resolution > 0
                              ? Math.max(1, Math.floor(identification.credit_budget / usage.credits_per_resolution))
                              : null;
                          const budgetBinds =
                            budgetCeiling !== null && (usage.plan_limit <= 0 || budgetCeiling <= usage.plan_limit);
                          const cycleEnds = usage.period_end
                            ? new Date(usage.period_end).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                            : null;
                          return (
                            <section className="space-y-3">
                              <div>
                                <h3 className="text-sm font-semibold">Visitor identification</h3>
                                <p className="mt-1 text-xs leading-5 text-muted-foreground">
                                  Reveal the companies and people behind anonymous traffic on your website. Each identified visitor uses credits.
                                </p>
                              </div>
                              {!rb2bTermsAccepted && (
                                <p className="rounded-md border border-dashed bg-muted/20 px-3 py-2 text-xs leading-5 text-muted-foreground">
                                  Accept the visitor identification terms above to turn identification on.
                                </p>
                              )}
                              <div className="divide-y overflow-hidden rounded-lg border bg-card">
                                <div className="flex items-center justify-between gap-4 p-4">
                                  <div className="flex min-w-0 items-center gap-3">
                                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border bg-muted/30 text-muted-foreground">
                                      <Fingerprint className="h-3.5 w-3.5" />
                                    </div>
                                    <div className="min-w-0">
                                      <span className="text-sm font-medium">Identify website visitors</span>
                                      <p className="mt-0.5 text-xs leading-5 text-muted-foreground">
                                        {identification.enabled
                                          ? 'On. Visitors on the websites below are identified.'
                                          : 'Off. Visitors stay anonymous and no identification credits are used.'}
                                      </p>
                                    </div>
                                  </div>
                                  <Switch
                                    className="shrink-0"
                                    checked={identification.enabled}
                                    disabled={identificationSaving || !rb2bTermsAccepted}
                                    onCheckedChange={(checked) =>
                                      updateIdentification(
                                        { enabled: checked },
                                        checked ? 'Visitor identification turned on' : 'Visitor identification turned off',
                                      )
                                    }
                                  />
                                </div>
                                <div className="p-4">
                                  <span className="text-sm font-medium">When to identify</span>
                                  <p className="mt-0.5 text-xs leading-5 text-muted-foreground">
                                    Choose how much of your traffic to spend identification credits on.
                                  </p>
                                  <div
                                    role="radiogroup"
                                    aria-label="When to identify visitors"
                                    className="mt-3 grid gap-2"
                                  >
                                    {IDENTIFICATION_TIMING_OPTIONS.map((option) => {
                                      const timing = identificationTimingFor(identification);
                                      const selected = timing === option.value;
                                      const locked = identificationSaving || !identification.enabled;
                                      return (
                                        <button
                                          key={option.value}
                                          type="button"
                                          role="radio"
                                          aria-checked={selected}
                                          disabled={locked}
                                          onClick={() => {
                                            if (selected) return;
                                            updateIdentification(option.patch, option.toast);
                                          }}
                                          className={cn(
                                            'flex w-full items-start gap-3 rounded-md border px-3 py-2.5 text-left transition-colors',
                                            selected ? 'border-foreground/40 bg-muted/40' : 'hover:border-foreground/15',
                                            locked && 'cursor-not-allowed opacity-60',
                                          )}
                                        >
                                          <span
                                            className={cn(
                                              'mt-1 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border',
                                              selected ? 'border-foreground' : 'border-muted-foreground/50',
                                            )}
                                          >
                                            {selected && <span className="h-1.5 w-1.5 rounded-full bg-foreground" />}
                                          </span>
                                          <span className="min-w-0">
                                            <span className="block text-sm font-medium">{option.label}</span>
                                            <span className="mt-0.5 block text-xs leading-5 text-muted-foreground">
                                              {option.description}
                                            </span>
                                          </span>
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                                <div className="grid gap-4 p-4 md:grid-cols-[190px_minmax(0,1fr)] md:gap-8">
                                  <div>
                                    <Label htmlFor="identificationBudget" className="text-sm font-semibold">Monthly budget</Label>
                                    <p className="mt-1 text-xs leading-5 text-muted-foreground">
                                      Credits identification may use per billing cycle. Leave blank for no budget.
                                    </p>
                                  </div>
                                  <div className="space-y-2">
                                    <div className="flex gap-2">
                                      <Input
                                        id="identificationBudget"
                                        type="number"
                                        min={0}
                                        step={1}
                                        inputMode="numeric"
                                        placeholder="No budget"
                                        className="max-w-[200px]"
                                        value={identificationBudgetInput}
                                        onChange={(e) => setIdentificationBudgetInput(e.target.value)}
                                        disabled={identificationSaving}
                                      />
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={handleSaveIdentificationBudget}
                                        disabled={identificationSaving}
                                      >
                                        Save budget
                                      </Button>
                                    </div>
                                    {usage.credits_per_resolution > 0 && (
                                      <p className="text-xs leading-5 text-muted-foreground">
                                        {formatWholeNumber(usage.credits_per_resolution)} credits per identified visitor
                                        {budgetCeiling !== null && ` · your budget covers about ${formatWholeNumber(budgetCeiling)} visitors`}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <div className="grid gap-4 p-4 md:grid-cols-[190px_minmax(0,1fr)] md:gap-8">
                                  <div>
                                    <Label htmlFor="identificationDomain" className="text-sm font-semibold">Websites</Label>
                                    <p className="mt-1 text-xs leading-5 text-muted-foreground">
                                      Identification only runs on these domains and their subdomains.
                                    </p>
                                  </div>
                                  <div className="space-y-2">
                                    <div className="flex gap-2">
                                      <Input
                                        id="identificationDomain"
                                        placeholder="www.example.com"
                                        className="max-w-[280px]"
                                        value={identificationDomainInput}
                                        onChange={(e) => setIdentificationDomainInput(e.target.value)}
                                        disabled={identificationSaving || !rb2bTermsAccepted}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAddIdentificationDomain();
                                          }
                                        }}
                                      />
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={handleAddIdentificationDomain}
                                        disabled={identificationSaving || !rb2bTermsAccepted || !identificationDomainInput.trim()}
                                      >
                                        Add
                                      </Button>
                                    </div>
                                    {identification.domains.length === 0 ? (
                                      <p className="text-xs leading-5 text-muted-foreground">
                                        No websites yet. Add the domain where your agent is installed.
                                      </p>
                                    ) : (
                                      <ul className="space-y-1">
                                        {identification.domains.map((domain) => (
                                          <li
                                            key={domain}
                                            className="flex items-center justify-between gap-2 rounded-md border bg-muted/20 px-3 py-1.5 text-xs"
                                          >
                                            <span className="flex min-w-0 items-center gap-2">
                                              <Globe className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                              <span className="truncate">{domain}</span>
                                            </span>
                                            <Button
                                              type="button"
                                              variant="ghost"
                                              size="sm"
                                              className="h-7 px-2 text-muted-foreground hover:text-destructive"
                                              onClick={() => handleRemoveIdentificationDomain(domain)}
                                              disabled={identificationSaving}
                                            >
                                              <Trash2 className="h-3.5 w-3.5" />
                                            </Button>
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                </div>
                                <div className="p-4">
                                  <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div>
                                      <span className="text-sm font-medium">This billing cycle</span>
                                      <p className="mt-0.5 text-xs leading-5 text-muted-foreground">
                                        {formatWholeNumber(usage.count)}
                                        {usage.limit > 0 ? ` of ${formatWholeNumber(usage.limit)}` : ''} visitors identified
                                        {usage.credits_per_resolution > 0 && ` · ${formatWholeNumber(usage.credits_used)} credits`}
                                        {usage.limit > 0 && (budgetBinds ? ' · capped by your budget' : ' · capped by your plan')}
                                      </p>
                                    </div>
                                    {usage.limit_exceeded ? (
                                      <Badge variant="destructive" className="text-[10px]">
                                        Paused until {cycleEnds ?? 'next cycle'}
                                      </Badge>
                                    ) : identification.enabled ? (
                                      <Badge variant="outline" className="text-[10px]">Active</Badge>
                                    ) : null}
                                  </div>
                                  {usage.limit_exceeded && (
                                    <p className="mt-2 text-xs leading-5 text-muted-foreground">
                                      {budgetBinds
                                        ? 'You reached your monthly identification budget. Raise it to keep identifying visitors this cycle.'
                                        : 'You reached the identified-visitor limit included in your plan. Contact us to raise it.'}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </section>
                          );
                        })()}

                        <SessionModerationSettings />
                      </div>
                    </TabsContent>
                  )}

                  {/* Team Management Tab */}
                  {userRole === 'admin' && (
                    <TabsContent value="org:team" className="mt-0">
                      <AgentTabHeader
                        icon={Users}
                        title="Team"
                        description="Manage members, invitations, and access requests."
                        actions={(
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => setIsInviteModalOpen(true)}
                            className="gap-2"
                          >
                            <UserPlus className="h-3.5 w-3.5" />
                            <span className="hidden sm:inline">Invite users</span>
                            <span className="sm:hidden">Invite</span>
                          </Button>
                        )}
                      />
                      <div className="mx-auto w-full max-w-4xl space-y-6 py-6">
                        <div className="flex flex-wrap gap-2">
                          <Badge variant="secondary" className="gap-1.5 px-2.5 py-1 text-xs font-medium">
                            <Users className="h-3.5 w-3.5" />
                            {organizationUsers.length} {organizationUsers.length === 1 ? 'member' : 'members'}
                          </Badge>
                        {pendingInvites.length > 0 && (
                            <Badge variant="outline" className="gap-1.5 px-2.5 py-1 text-xs font-medium">
                              <Mail className="h-3.5 w-3.5" />
                              {pendingInvites.length} pending
                            </Badge>
                        )}
                        </div>

                        <div className="overflow-x-auto rounded-lg border bg-card">
                          <Table className="min-w-[640px]">
                          <TableHeader>
                              <TableRow className="bg-muted/30 hover:bg-muted/30">
                              <TableHead>Member</TableHead>
                              <TableHead>Email</TableHead>
                              <TableHead>Status</TableHead>
                                <TableHead className="w-12">
                                  <span className="sr-only">Actions</span>
                                </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {tableData.map((row) => (
                              <TableRow key={row.id} className="hover:bg-muted/30">
                                <TableCell>
                                  <div className="flex items-center gap-3">
                                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border bg-muted/30 text-xs font-medium text-muted-foreground">
                                      {isUser(row) 
                                        ? (row.first_name?.[0] || row.email[0]).toUpperCase()
                                        : '?'}
                                    </div>
                                      <span className="text-sm font-medium">
                                      {isUser(row) 
                                        ? `${row.first_name || ''} ${row.last_name || ''}`.trim() || 'Unnamed'
                                        : 'Pending User'}
                                    </span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                  {row.email}
                                </TableCell>
                                <TableCell>
                                  {isUser(row) ? (
                                      <Badge variant="outline" className="capitalize">
                                      {row.role}
                                    </Badge>
                                  ) : (
                                    row.status === 'access_requested' ? (
                                        <Badge variant="outline">
                                        Access Requested
                                      </Badge>
                                    ) : (
                                        <Badge variant="secondary">
                                        Invite Pending
                                      </Badge>
                                    )
                                  )}
                                </TableCell>
                                <TableCell>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button 
                                        variant="ghost" 
                                          size="icon"
                                          type="button"
                                        className="h-8 w-8 p-0"
                                          aria-label={`Actions for ${row.email}`}
                                      >
                                        <MoreHorizontal className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      {!isUser(row) ? (
                                        <>
                                          {(row as PendingInvite & { type: 'invite' }).status === 'access_requested' ? (
                                            <DropdownMenuItem
                                              onClick={() => handleApproveAccessRequest(row as PendingInvite & { type: 'invite' })}
                                              className="cursor-pointer gap-2"
                                            >
                                              <UserCheck className="h-4 w-4" />
                                              Approve Request
                                            </DropdownMenuItem>
                                          ) : (
                                            <DropdownMenuItem 
                                              onClick={() => handleResendInvite(row as PendingInvite & { type: 'invite' })}
                                              className="cursor-pointer gap-2"
                                            >
                                              <Mail className="h-4 w-4" />
                                              Resend Invite
                                            </DropdownMenuItem>
                                          )}
                                          <DropdownMenuItem 
                                            onClick={() => handleDeleteInvite(row as PendingInvite & { type: 'invite' })}
                                            className="cursor-pointer text-red-600 focus:text-red-600 gap-2"
                                          >
                                            <Trash2 className="h-4 w-4" />
                                            {(row as PendingInvite & { type: 'invite' }).status === 'access_requested' ? 'Delete Request' : 'Delete Invite'}
                                          </DropdownMenuItem>
                                        </>
                                      ) : (
                                        <>
                                          <DropdownMenuItem 
                                            onClick={() => handleOpenEditDialog(row as UserProfileType)}
                                            className="cursor-pointer gap-2"
                                          >
                                            <Settings className="h-4 w-4" />
                                            Edit User
                                          </DropdownMenuItem>
                                          <DropdownMenuItem 
                                            onClick={() => handleRemoveUserAccess(row as UserProfileType)}
                                            className="cursor-pointer text-red-600 focus:text-red-600 gap-2"
                                          >
                                            <Trash2 className="h-4 w-4" />
                                            Remove Access
                                          </DropdownMenuItem>
                                        </>
                                      )}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        </div>
                      </div>
                    </TabsContent>
                  )}

                  {/* Lifecycle Stages Tab */}
                  {userRole === 'admin' && (
                    <TabsContent value="org:stages" className="mt-0">
                      <LifecycleStages />
                    </TabsContent>
                  )}

                  {/* Billing Tab */}
                  {userRole === 'admin' && (
                    <TabsContent value="org:billing" className="mt-0">
                      <BillingTab />
                    </TabsContent>
                  )}
                </div>
              </ScrollArea>
            </div>
          </Tabs>
        </div>

        {/* Edit User Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-[760px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg">
                <User className="h-4 w-4 text-muted-foreground" />
                Edit User: {selectedUser?.first_name} {selectedUser?.last_name}
              </DialogTitle>
              <DialogDescription>
                Modify the user's role, notification preferences, and working hours.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-8 py-4">
              {/* Role Selection */}
              <section className="space-y-3">
                <div>
                  <h3 className="text-sm font-semibold">Role</h3>
                  <p className="mt-1 text-xs leading-5 text-muted-foreground">Control this member&apos;s organization permissions.</p>
                </div>
                <div className="rounded-lg border bg-card p-4">
                  <Select value={editedUserRole} onValueChange={setEditedUserRole}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="member">Member</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </section>

              {/* Notification Preferences */}
              <section className="space-y-3">
                <div>
                  <h3 className="text-sm font-semibold">Notification preferences</h3>
                  <p className="mt-1 text-xs leading-5 text-muted-foreground">Choose which activity sends this member an email.</p>
                </div>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  {NOTIFICATIONS.map((notification) => (
                    <NotificationToggle
                      key={notification.key}
                      notificationKey={notification.key}
                      label={notification.label}
                      description={notification.description}
                      icon={notification.icon}
                      isEnabled={editedNotifications[notification.key]}
                      onChange={updateEditedNotification}
                    />
                  ))}
                </div>
              </section>

              {/* Working Hours */}
              <section className="space-y-3">
                <div>
                  <h3 className="text-sm font-semibold">Working hours</h3>
                  <p className="mt-1 text-xs leading-5 text-muted-foreground">Limit this member&apos;s escalation availability.</p>
                </div>
                
                <div className="flex items-center justify-between gap-4 rounded-lg border bg-card p-4">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border bg-muted/30 text-muted-foreground">
                      <Clock className="h-3.5 w-3.5" />
                    </div>
                    <div className="min-w-0">
                      <span className="text-sm font-medium">Enable working hours</span>
                      <p className="mt-0.5 text-xs leading-5 text-muted-foreground">
                        Limit escalation availability to specific hours
                      </p>
                    </div>
                  </div>
                  <Switch
                    className="shrink-0"
                    checked={editedAvailabilityEnabled}
                    onCheckedChange={setEditedAvailabilityEnabled}
                  />
                </div>

                {editedAvailabilityEnabled && (
                  <div className="divide-y overflow-hidden rounded-lg border bg-card">
                    <div className="space-y-2 p-4">
                      <Label className="text-xs font-medium">Time zone</Label>
                      <Select value={editedAvailabilityTimezone} onValueChange={setEditedAvailabilityTimezone}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a time zone" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[320px]">
                          {timeZones.map((tz) => (
                            <SelectItem key={tz} value={tz}>
                              {tz}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                        {DAY_KEYS.map((day, index) => (
                          <div 
                            key={day} 
                            className={cn(
                              "grid gap-3 p-3 sm:grid-cols-[100px_minmax(0,1fr)] sm:items-center",
                              index !== DAY_KEYS.length - 1 && "border-b",
                              editedAvailabilityWeekly[day].enabled 
                                ? "bg-background" 
                                : "bg-muted/20"
                            )}
                          >
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={editedAvailabilityWeekly[day].enabled}
                                onCheckedChange={(checked) => updateEditedAvailabilityDay(day, { enabled: checked })}
                              />
                              <span className={cn(
                                "text-xs font-medium",
                                !editedAvailabilityWeekly[day].enabled && "text-muted-foreground"
                              )}>
                                {DAY_LABELS[day].slice(0, 3)}
                              </span>
                            </div>
                            <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2">
                              <Input
                                type="time"
                                value={editedAvailabilityWeekly[day].start}
                                disabled={!editedAvailabilityWeekly[day].enabled}
                                onChange={(e) => updateEditedAvailabilityDay(day, { start: e.target.value })}
                                className="h-8 min-w-0 text-sm"
                              />
                              <span className="text-xs text-muted-foreground">to</span>
                              <Input
                                type="time"
                                value={editedAvailabilityWeekly[day].end}
                                disabled={!editedAvailabilityWeekly[day].enabled}
                                onChange={(e) => updateEditedAvailabilityDay(day, { end: e.target.value })}
                                className="h-8 min-w-0 text-sm"
                              />
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </section>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="button" onClick={handleUserUpdate}>
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </form>

      {upgradeModalVisible && (
        <Upgrade 
          showModal={upgradeModalVisible} 
          setShowModal={setUpgradeModalVisible} 
          sessionId={null} 
          upgradeSuccess={false}
        />
      )}

      <InviteModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onInvitesSent={handleInvitesSent}
      />
    </div>
  );
}

export default UserProfile;
